
import { AdminRouteGuard } from "@/components/admin/AdminRouteGuard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminSettingsPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [siteSettings, setSiteSettings] = useState({
    siteName: "Property LK",
    siteDescription: "Sri Lanka's premier commercial property marketplace",
    contactEmail: "admin@propertylk.com",
    supportPhone: "+94 71 234 5678",
    maintenanceMode: false,
    allowRegistrations: true,
    defaultCurrency: "USD"
  });
  
  const [listingSettings, setListingSettings] = useState({
    requireApproval: true,
    maxImages: 10,
    maxFeaturedListings: 5,
    listingExpiryDays: 30,
    allowRatings: true,
    allowComments: true
  });
  
  const [emailSettings, setEmailSettings] = useState({
    smtpServer: "smtp.example.com",
    smtpPort: "587",
    smtpUsername: "notifications@propertylk.com",
    smtpPassword: "********",
    fromEmail: "no-reply@propertylk.com",
    fromName: "Property LK",
    emailVerificationRequired: true,
    sendWelcomeEmail: true
  });

  const handleSiteSettingsChange = (field: string, value: string | boolean | number) => {
    setSiteSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleListingSettingsChange = (field: string, value: string | boolean | number) => {
    setListingSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleEmailSettingsChange = (field: string, value: string | boolean | number) => {
    setEmailSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
      console.error("Error saving settings:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminRouteGuard requiredRole="superadmin">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">System Settings</h1>
          <p className="text-muted-foreground">
            Configure system-wide settings and preferences for the platform
          </p>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general">General Settings</TabsTrigger>
            <TabsTrigger value="listings">Listing Settings</TabsTrigger>
            <TabsTrigger value="email">Email Configuration</TabsTrigger>
          </TabsList>
          
          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Site Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="siteName">Site Name</Label>
                    <Input 
                      id="siteName" 
                      value={siteSettings.siteName}
                      onChange={(e) => handleSiteSettingsChange('siteName', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contactEmail">Contact Email</Label>
                    <Input 
                      id="contactEmail" 
                      type="email" 
                      value={siteSettings.contactEmail}
                      onChange={(e) => handleSiteSettingsChange('contactEmail', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="siteDescription">Site Description</Label>
                    <Input 
                      id="siteDescription" 
                      value={siteSettings.siteDescription}
                      onChange={(e) => handleSiteSettingsChange('siteDescription', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="supportPhone">Support Phone</Label>
                    <Input 
                      id="supportPhone" 
                      value={siteSettings.supportPhone}
                      onChange={(e) => handleSiteSettingsChange('supportPhone', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="defaultCurrency">Default Currency</Label>
                    <Select 
                      value={siteSettings.defaultCurrency} 
                      onValueChange={(value) => handleSiteSettingsChange('defaultCurrency', value)}
                    >
                      <SelectTrigger id="defaultCurrency">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="LKR">LKR (Rs)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Separator className="my-6" />
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="maintenanceMode">Maintenance Mode</Label>
                      <p className="text-sm text-muted-foreground">
                        When enabled, the site will show a maintenance message to visitors
                      </p>
                    </div>
                    <Switch 
                      id="maintenanceMode" 
                      checked={siteSettings.maintenanceMode}
                      onCheckedChange={(checked) => handleSiteSettingsChange('maintenanceMode', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="allowRegistrations">Allow User Registrations</Label>
                      <p className="text-sm text-muted-foreground">
                        When disabled, new users cannot register on the platform
                      </p>
                    </div>
                    <Switch 
                      id="allowRegistrations" 
                      checked={siteSettings.allowRegistrations}
                      onCheckedChange={(checked) => handleSiteSettingsChange('allowRegistrations', checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="listings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Listing Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="maxImages">Maximum Images Per Listing</Label>
                    <Input 
                      id="maxImages" 
                      type="number" 
                      min="1"
                      value={listingSettings.maxImages}
                      onChange={(e) => handleListingSettingsChange('maxImages', parseInt(e.target.value))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="maxFeaturedListings">Maximum Featured Listings</Label>
                    <Input 
                      id="maxFeaturedListings" 
                      type="number"
                      min="1"
                      value={listingSettings.maxFeaturedListings}
                      onChange={(e) => handleListingSettingsChange('maxFeaturedListings', parseInt(e.target.value))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="listingExpiryDays">Listing Expiry (Days)</Label>
                    <Input 
                      id="listingExpiryDays" 
                      type="number"
                      min="1"
                      value={listingSettings.listingExpiryDays}
                      onChange={(e) => handleListingSettingsChange('listingExpiryDays', parseInt(e.target.value))}
                    />
                  </div>
                </div>
                
                <Separator className="my-6" />
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="requireApproval">Require Admin Approval</Label>
                      <p className="text-sm text-muted-foreground">
                        When enabled, new listings require admin approval before being published
                      </p>
                    </div>
                    <Switch 
                      id="requireApproval" 
                      checked={listingSettings.requireApproval}
                      onCheckedChange={(checked) => handleListingSettingsChange('requireApproval', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="allowRatings">Allow Property Ratings</Label>
                      <p className="text-sm text-muted-foreground">
                        When enabled, users can rate listed properties
                      </p>
                    </div>
                    <Switch 
                      id="allowRatings" 
                      checked={listingSettings.allowRatings}
                      onCheckedChange={(checked) => handleListingSettingsChange('allowRatings', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="allowComments">Allow Property Comments</Label>
                      <p className="text-sm text-muted-foreground">
                        When enabled, users can comment on property listings
                      </p>
                    </div>
                    <Switch 
                      id="allowComments" 
                      checked={listingSettings.allowComments}
                      onCheckedChange={(checked) => handleListingSettingsChange('allowComments', checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="email" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Email Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="smtpServer">SMTP Server</Label>
                    <Input 
                      id="smtpServer" 
                      value={emailSettings.smtpServer}
                      onChange={(e) => handleEmailSettingsChange('smtpServer', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="smtpPort">SMTP Port</Label>
                    <Input 
                      id="smtpPort" 
                      value={emailSettings.smtpPort}
                      onChange={(e) => handleEmailSettingsChange('smtpPort', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="smtpUsername">SMTP Username</Label>
                    <Input 
                      id="smtpUsername" 
                      value={emailSettings.smtpUsername}
                      onChange={(e) => handleEmailSettingsChange('smtpUsername', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="smtpPassword">SMTP Password</Label>
                    <Input 
                      id="smtpPassword" 
                      type="password" 
                      value={emailSettings.smtpPassword}
                      onChange={(e) => handleEmailSettingsChange('smtpPassword', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="fromEmail">From Email</Label>
                    <Input 
                      id="fromEmail" 
                      type="email" 
                      value={emailSettings.fromEmail}
                      onChange={(e) => handleEmailSettingsChange('fromEmail', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="fromName">From Name</Label>
                    <Input 
                      id="fromName" 
                      value={emailSettings.fromName}
                      onChange={(e) => handleEmailSettingsChange('fromName', e.target.value)}
                    />
                  </div>
                </div>
                
                <Separator className="my-6" />
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="emailVerificationRequired">Require Email Verification</Label>
                      <p className="text-sm text-muted-foreground">
                        When enabled, new users must verify their email before using the platform
                      </p>
                    </div>
                    <Switch 
                      id="emailVerificationRequired" 
                      checked={emailSettings.emailVerificationRequired}
                      onCheckedChange={(checked) => handleEmailSettingsChange('emailVerificationRequired', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="sendWelcomeEmail">Send Welcome Email</Label>
                      <p className="text-sm text-muted-foreground">
                        When enabled, new users receive a welcome email after registration
                      </p>
                    </div>
                    <Switch 
                      id="sendWelcomeEmail" 
                      checked={emailSettings.sendWelcomeEmail}
                      onCheckedChange={(checked) => handleEmailSettingsChange('sendWelcomeEmail', checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-end">
          <Button 
            onClick={handleSaveSettings}
            disabled={isSubmitting}
            size="lg"
          >
            {isSubmitting ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>
    </AdminRouteGuard>
  );
}
